import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  InputGroup,
  Row,
  Tab,
  Tabs,
  Badge,
} from "react-bootstrap";
import { ThreeDot } from "react-loading-indicators";
import {
  formatCurrency,
  formatToPHP,
  formatToTwoDecimal,
} from "../../utils/numberFormatter";
import { customStyles } from "../styles/table-style";
import { useServerPagination } from "../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../hooks/customHook/paginationHook/usePagination";
import { getMonthBoundaries } from "../../utils/newdate";
import { CashFlowView } from "./components/CashFlow/CashFlowView";
import axios from "axios";
import Select from "react-select";
import DataTable from "react-data-table-component";
import NoAccess from "../../assets/img/NoAccess.png";
import BASE_URL from "../../assets/global/url";
import DateRangePicker from "../../components/DateRangePicker";
import FilterStore from "../../hooks/customHook/FilterStore";
import useDarkMode from "../../components/Themes/Usedarkmode";
import useDecodeToken from "../../hooks/customHook/useDecodeToken";
import {
  formatDateForAPI,
  formatDateForColumn,
} from "../../utils/dateFormatter";
import { useServerSort } from "../../hooks/customHook/sortingHook/useTableSort";

const CASH_FLOW_TRANSACTION_URL = `${BASE_URL}/cashFlow/v2/getCashFlow`;
const CASH_FLOW_WIDGET = `${BASE_URL}/cashFlow/v2/getCashFlowWidget`;
const CASH_FLOW_ACCOUNT = `${BASE_URL}/cashFlow/v2/getAccountListSub3`;
const DEBOUNCE_500 = 500;
// const DEBOUNCE_300 = 300;
const filterOptions = [
  "All",
  "Account",
  "Transaction Number",
  "Issued To",
  "Module From",
  "Description",
  "Amount",
  "Status",
];

const boundaries = getMonthBoundaries();

const CASH_FLOW_TABS = ["confirmation", "transaction"];
const DEFAULT_TAB = "confirmation";

// Only accept a saved tab if it is actually one of this page's tabs.
// Otherwise (nothing saved / stale or foreign value) -> Confirmation.
const getInitialTab = () => {
  const saved = FilterStore.getTab();
  return CASH_FLOW_TABS.includes(saved) ? saved : DEFAULT_TAB;
};

const getBadgeConfig = (status) => {
  const statusMap = {
    pending: "warning",
    transferred: "success",
    paid: "success",
    collected: "success",
    approved: "success",
    rejected: "danger",
    failed: "danger",
    cancelled: "danger",
  };

  const variant = statusMap[status?.toLowerCase()] || "secondary";

  return {
    bg: variant,
    className: `fs-6 px-4 py-2 rounded-pill bg-opacity-10 text-${variant} border border-${variant} border-opacity-25`,
  };
};

const NewCashFlow = ({ authrztn }) => {
  const { themeSelectStyles } = useDarkMode();
  const userLoggedID = useDecodeToken();

  const [dateRange, setDateRange] = useState(() => {
    const savedFilter = FilterStore.get();

    if (savedFilter && savedFilter.from && savedFilter.to) {
      return {
        start: formatDateForAPI(savedFilter.from),
        end: formatDateForAPI(savedFilter.to),
      };
    }

    return {
      start: formatDateForAPI(boundaries.start),
      end: formatDateForAPI(boundaries.end),
    };
  });
  const [widgetData, setWidgetData] = useState({
    totalIn: 0,
    totalOut: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [searchText, setSearchText] = useState(
    () => FilterStore.CFgetSearch() || "",
  );
  const [filterColumn, setFilterColumn] = useState(
    () => FilterStore.CFgetFilterColumn() || "All",
  );
  const [accountOptions, setAccountOptions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(() => {
    const saved = FilterStore.getAccountFilter();
    return saved && saved !== ""
      ? saved
      : { value: "", label: "All Cash Accounts" };
  });
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);

  const debounceTimer = useRef(null);

  const pagination = useServerPagination(
    CASH_FLOW_TRANSACTION_URL,
    FilterStore.CFgetPerPage(),
    {
      startDate: dateRange.start,
      endDate: dateRange.end,
      searchText: searchText,
      filterColumn: filterColumn,
      accountName: selectedAccount?.value || "",
      status: activeTab === "confirmation" ? "Pending" : "NotPending",
    },
  );

  // No initial sortBy: every header starts neutral until the user picks one
  const sort = useServerSort(pagination);

  const pageRestored = useRef(false);

  useEffect(() => {
    if (!pagination.loading) {
      setIsLoading(false);
    }
  }, [pagination.loading]);

  // --- Page / PerPage retention ---
  useEffect(() => {
    if (!pageRestored.current) return;
    if (pagination.currentPage) {
      FilterStore.CFsavePage(pagination.currentPage);
    }
  }, [pagination.currentPage]);

  useEffect(() => {
    if (pagination.itemsPerPage) {
      FilterStore.CFsavePerPage(pagination.itemsPerPage);
    }
  }, [pagination.itemsPerPage]);

  useEffect(() => {
    const savedPage = FilterStore.CFgetPage();
    if (savedPage > 1) {
      setTimeout(() => {
        pagination.setCurrentPage(savedPage);
        pageRestored.current = true;
      }, 600);
    } else {
      setTimeout(() => {
        pageRestored.current = true;
      }, 600);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWidgetData = useCallback(async () => {
    if (!dateRange.start || !dateRange.end) return;

    try {
      const res = await axios.get(CASH_FLOW_WIDGET, {
        params: {
          startDate: dateRange.start,
          endDate: dateRange.end,
          accountName: selectedAccount?.value || "",
          status: activeTab === "confirmation" ? "Pending" : "NotPending",
          filterColumn: filterColumn,
          searchText: searchText,
        },
      });
      setWidgetData({
        totalIn: res.data.totalIn || 0,
        totalOut: res.data.totalOut || 0,
      });
    } catch (error) {
      console.error("Error fetching widget data:", error);
    }
  }, [
    dateRange.start,
    dateRange.end,
    selectedAccount?.value,
    activeTab,
    filterColumn,
    searchText,
  ]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchWidgetData();
    }, DEBOUNCE_500);

    return () => clearTimeout(timeoutId);
  }, [fetchWidgetData]);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await axios.get(CASH_FLOW_ACCOUNT);

        const options = res.data.map((acc) => ({
          value: acc.id,
          label: `${acc.account_list_base_sub?.module_type || "Unknown"} - ${acc.account_name}`,
        }));

        setAccountOptions([
          { value: "", label: "All Cash Accounts" },
          ...options,
        ]);
      } catch (error) {
        console.error("Error fetching account list:", error);
      }
    };

    fetchAccounts();
  }, []);

  const handleDateRangeChange = (startDate, endDate) => {
    let from = startDate;
    let to = endDate;

    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      from = start;
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      to = end;
    }

    const formattedStart = formatDateForAPI(from);
    const formattedEnd = formatDateForAPI(to);

    setDateRange({ start: formattedStart, end: formattedEnd });

    FilterStore.save(formattedStart, formattedEnd);

    pagination.updateParams({
      startDate: formattedStart,
      endDate: formattedEnd,
    });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    FilterStore.CFsaveSearch(value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      pagination.updateParams({
        searchText: value,
        filterColumn: filterColumn,
      });
    }, DEBOUNCE_500);
  };

  const handleFilterColumnChange = (column) => {
    setFilterColumn(column);
    FilterStore.CFsaveFilterColumn(column);

    pagination.updateParams({
      searchText: searchText,
      filterColumn: column,
    });
  };

  const handleAccountChange = (selectedOption) => {
    setSelectedAccount(selectedOption);
    FilterStore.saveAccountFilter(selectedOption);

    pagination.updateParams({
      accountName: selectedOption?.value || "",
    });
  };

  const handleTabChange = (k) => {
    setActiveTab(k);
    FilterStore.saveTab(k);

    pagination.updateParams({
      status: k === "confirmation" ? "Pending" : "NotPending",
    });
  };

  const handleViewAction = (row) => {
    setSelectedTransactionId(row.id);
    setShowViewModal(true);
  };

  const handleRowClick = (row) => {
    handleViewAction(row);
  };

  const handleClearFilters = () => {
    const defaultStart = formatDateForAPI(boundaries.start);
    const defaultEnd = formatDateForAPI(boundaries.end);
    const defaultAccount = { value: "", label: "All Cash Accounts" };
    const defaultColumn = "All";
    const defaultSearch = "";

    FilterStore.clear();
    FilterStore.save(defaultStart, defaultEnd);
    FilterStore.saveAccountFilter("");
    FilterStore.CFsaveFilterColumn(defaultColumn);
    FilterStore.CFsaveSearch(defaultSearch);

    setDateRange({ start: defaultStart, end: defaultEnd });
    setSelectedAccount(defaultAccount);
    setFilterColumn(defaultColumn);
    setSearchText(defaultSearch);

    pagination.updateParams({
      startDate: defaultStart,
      endDate: defaultEnd,
      accountName: "",
      filterColumn: defaultColumn,
      searchText: defaultSearch,
      status: activeTab === "confirmation" ? "Pending" : "NotPending",
    });
  };

  const columns = useMemo(() => {
    const getCalculatedAmount = (row) => {
      const isPositive =
        (row.account_list_id_cash_froms?.account_name &&
          row.module_from === "Collection Check") ||
        (row.transaction_number?.includes("TRANSFER-") &&
          row.account_list_id_cash_tos == null) ||
        !row.account_list_id_cash_froms?.account_name;

      let amount = row.amount || 0;
      const rate = row.rate || 1;
      const senderCurr =
        row.account_list_id_cash_froms?.currency?.currency_name?.toUpperCase();
      const receiverCurr =
        row.account_list_id_cash_tos?.currency?.currency_name?.toUpperCase();

      if (
        !isPositive &&
        senderCurr &&
        receiverCurr &&
        senderCurr !== receiverCurr
      ) {
        if (senderCurr === "PHP" && receiverCurr !== "PHP") {
          return amount * rate;
        } else if (senderCurr !== "PHP" && receiverCurr === "PHP") {
          return amount / rate;
        }
      }

      return amount;
    };

    return [
      {
        name: "Account",
        selector: (row) =>
          row.account_list_id_cash_froms?.account_name
            ? row.account_list_id_cash_froms?.account_name
            : "---",
        sortField: "account_list_id_cash_froms",
      },
      {
        name: "Date",
        selector: (row) =>
          row.transaction_date
            ? formatDateForColumn(row.transaction_date)
            : "---",
        sortField: "transaction_date",
      },
      {
        name: "Transaction Number",
        selector: (row) =>
          row.transaction_number ? row.transaction_number : "---",
        sortField: "transaction_number",
      },
      {
        name: "Issued To",
        selector: (row) =>
          row.account_list_id_cash_tos?.account_name
            ? row.account_list_id_cash_tos?.account_name
            : "---",
        sortField: "account_list_id_cash_tos",
      },
      {
        name: "Module From",
        selector: (row) => (row.module_from ? row.module_from : "---"),
        sortField: "module_from",
      },
      {
        name: "Description",
        selector: (row) => (row.description ? row.description : "---"),
        sortField: "description",
      },
      {
        name: "Amount",
        selector: (row) => formatToTwoDecimal(getCalculatedAmount(row)),
        sortField: "amount",
        cell: (row) => {
          const isPositive =
            (row.account_list_id_cash_froms?.account_name &&
              row.module_from === "Collection Check") ||
            (row.transaction_number?.includes("TRANSFER-") &&
              row.account_list_id_cash_tos == null) ||
            !row.account_list_id_cash_froms?.account_name;

          const displayAmount = getCalculatedAmount(row);

          const senderCurr =
            row.account_list_id_cash_froms?.currency?.currency_name;
          const receiverCurr =
            row.account_list_id_cash_tos?.currency?.currency_name;

          const displayCurrency = isPositive
            ? receiverCurr || senderCurr || "PHP"
            : senderCurr || receiverCurr || "PHP";

          return (
            <div className={isPositive ? "text-primary" : "text-danger"}>
              {isPositive ? "+" : "-"}
              {formatCurrency(displayAmount, displayCurrency)}
            </div>
          );
        },
      },
      {
        name: "Status",
        selector: (row) => row.status || "---",
        sortField: "status",
        cell: (row) =>
          row.status ? (
            <Badge {...getBadgeConfig(row.status)}>{row.status}</Badge>
          ) : (
            "---"
          ),
      },
      {
        name: "Action",
        cell: (row) => (
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleViewAction(row)}
            title="View Details">
            <i className="fa-solid fa-eye" />
          </Button>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "80px",
      },
    ];
  }, []);

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      {isLoading ? (
        <div className="loading-container">
          <ThreeDot
            variant="brick-stack"
            color="#6290FE"
            size="large"
            text="Loading Data..."
            textColor=""
          />
        </div>
      ) : authrztn.includes("CashFlow-View") ? (
        <Container fluid>
          {/* TITLE */}
          <div className="w-100 p-2 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3">CASH FLOW</span>
            </div>
          </div>

          {/* WIDGET */}
          <Row className="mt-3">
            <Col md={6}>
              <Card className="text-center">
                <Card.Body>
                  <Card.Title className="d-flex align-items-center justify-content-center">
                    <i className="bx bx-money me-1 h-100" />
                    Total In
                  </Card.Title>
                  <Card.Text
                    className="amount"
                    title={formatToPHP(widgetData.totalIn)}
                    // Same green as the Customer widgets (lighter in dark mode)
                    style={{ color: "var(--widget-number-color)" }}>
                    {formatToPHP(widgetData.totalIn)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="text-center">
                <Card.Body>
                  <Card.Title className="d-flex align-items-center justify-content-center">
                    <i className="bx bxs-credit-card me-1 h-100" />
                    Total Out
                  </Card.Title>
                  <Card.Text
                    className="amount"
                    title={formatToPHP(widgetData.totalOut)}
                    // Same green as the Customer widgets (lighter in dark mode)
                    style={{ color: "var(--widget-number-color)" }}>
                    {formatToPHP(widgetData.totalOut)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* MAIN ACTIONS */}
          <Row className="mt-3 mb-3">
            <Col sm={3}>
              <Form.Label>Cash Account Only</Form.Label>
              <Select
                options={accountOptions}
                value={selectedAccount}
                onChange={handleAccountChange}
                placeholder="Select Account..."
                isClearable={false}
                className="react-select-container"
                classNamePrefix="react-select"
                styles={themeSelectStyles({})}
              />
            </Col>
            <Col sm>
              <DateRangePicker
                startDate={dateRange.start}
                endDate={dateRange.end}
                onDateRangeChange={handleDateRangeChange}
                label="Date Range"
              />
            </Col>
            <Col sm>
              <Form.Label
                className="d-none d-sm-block"
                style={{ visibility: "hidden" }}>
                Clear Action
              </Form.Label>
              <Button
                variant="primary"
                className="w-100"
                onClick={handleClearFilters}>
                <i className="bx bx-reset me-1" /> Clear All Filters
              </Button>
            </Col>
          </Row>

          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder="Search"
              value={searchText}
              onChange={handleSearchChange}
            />
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-search">
                <i className="fa-solid fa-sliders me-2" />
                {filterColumn}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {filterOptions.map((option) => (
                  <Dropdown.Item
                    key={option}
                    onClick={() => handleFilterColumnChange(option)}
                    active={filterColumn === option}>
                    {option}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </InputGroup>

          <Tabs
            activeKey={activeTab}
            onSelect={handleTabChange}
            transition={false}
            mountOnEnter
            unmountOnExit={false}
            id="cash-flow-tabs"
            className="mb-3 mt-3">
            <Tab eventKey="confirmation" title="Confirmation">
              <div className="data-table-cell-width">
                <DataTable
                  columns={sort.sortColumns(columns)}
                  data={pagination.data}
                  customStyles={customStyles}
                  className="dataTable pointer-cursor-rows"
                  onRowClicked={handleRowClick}
                  pointerOnHover
                  highlightOnHover
                  {...sort.sortProps}
                />
              </div>
            </Tab>
            <Tab eventKey="transaction" title="Transactions">
              <div className="data-table-cell-width">
                <DataTable
                  columns={sort.sortColumns(columns)}
                  data={pagination.data}
                  customStyles={customStyles}
                  className="dataTable"
                  onRowClicked={handleRowClick}
                  pointerOnHover
                  highlightOnHover
                  {...sort.sortProps}
                />
              </div>
            </Tab>
          </Tabs>
          <PaginationControls {...pagination} />

          <CashFlowView
            show={showViewModal}
            onHide={() => setShowViewModal(false)}
            transactionId={selectedTransactionId}
            userLoggedID={userLoggedID}
            onSuccess={() => pagination.refreshData()}
            getBadgeConfig={getBadgeConfig}
          />
        </Container>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}
    </div>
  );
};

export { NewCashFlow };
